import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/Login.vue'),
      meta: { requiresAuth: false, layout: 'blank' }
    },
    {
      path: '/sso/callback',
      name: 'auth-callback',
      component: () => import('@/pages/AuthCallback.vue'),
      meta: { requiresAuth: false, layout: 'blank' }
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/pages/Dashboard.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates',
      name: 'templates',
      component: () => import('@/pages/Templates.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/presentations',
      name: 'presentations',
      component: () => import('@/pages/Presentations.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/workflows',
      name: 'workflows',
      component: () => import('@/pages/Workflows.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/collections',
      name: 'collections',
      component: () => import('@/pages/Collections.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      component: () => import('@/pages/settings/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      redirect: '/settings/profile',
      children: [
        {
          path: 'profile',
          name: 'settings-profile',
          component: () => import('@/pages/settings/ProfileSettings.vue'),
        },
        {
          path: 'api-keys',
          name: 'settings-api-keys',
          component: () => import('@/pages/settings/ApiKeysSettings.vue'),
        },
        {
          path: 'users',
          name: 'settings-users',
          component: () => import('@/pages/settings/UsersSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'scopes',
          name: 'settings-scopes',
          component: () => import('@/pages/settings/ScopesSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'executions',
          name: 'settings-executions',
          component: () => import('@/pages/settings/ExecutionsSettings.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'about',
          name: 'settings-about',
          component: () => import('@/pages/settings/AboutSettings.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/'
    }
  ]
})

// Navigation guard for authentication
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Check if route requires authentication
  const requiresAuth = to.meta.requiresAuth !== false

  if (requiresAuth && !authStore.isAuthenticated) {
    // Try to validate existing token
    if (authStore.accessToken) {
      try {
        await authStore.checkAuth()
        if (authStore.isAuthenticated) {
          // Continue to admin check below
        } else {
          next({
            name: 'login',
            query: { redirect: to.fullPath }
          })
          return
        }
      } catch {
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
    } else {
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // Check admin-only routes
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'settings-profile' })
    return
  }

  // If authenticated user tries to access login page, redirect to dashboard
  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
